import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';

// Layouts
import SuperAdminSidebar from './components/SuperAdminSidebar';
import Sidebar from './components/Sidebar';
import SupportAgentSidebar from './components/SupportAgentSidebar';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Gateway from './pages/Gateway';
import Broadcaster from './pages/Broadcaster';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import Organizations from './pages/Organizations';
import Inquiries from './pages/Inquiries';
import AuditLogs from './pages/AuditLogs';
import Dashboard from './pages/Dashboard';
import BotManagement from './pages/BotManagement';
import Chats from './pages/Chats';
import Leads from './pages/Leads';
import Users from './pages/Users';
import FollowUps from './pages/FollowUps';
import Integrations from './pages/Integrations';
import SupportTickets from './pages/SupportTickets';
import SupportConfig from './pages/SupportConfig';

// ─── Super Admin Layout ───────────────────────────────────────────────────────
function SuperAdminLayout() {
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0f172a' }}>
      <SuperAdminSidebar />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Routes>
          <Route path="/"              element={<SuperAdminDashboard />} />
          <Route path="/organizations" element={<Organizations />} />
          <Route path="/users"         element={<Users />} />
          <Route path="/inquiries"     element={<Inquiries />} />
          <Route path="/audit-logs"    element={<AuditLogs />} />
          <Route path="*"              element={<Navigate to="/app" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// ─── Org Layout (OrgAdmin + User) ─────────────────────────────────────────────
function OrgLayout() {
  const { user } = useAuth();
  const features = user?.allowedFeatures || [];

  function featureRoute(feature, path, element) {
    if (!features.includes(feature)) return null;
    return <Route path={path} element={element} />;
  }

  const isOrgAdmin = user?.role === 'orgadmin';

  return (
    <SocketProvider>
      <div style={{ display: 'flex', minHeight: '100vh', background: '#0f172a' }}>
        <Sidebar />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Routes>
            {featureRoute('dashboard', '/',           <Dashboard />)}
            {featureRoute('whatsapp',  '/bot-config', <BotManagement />)}
            {featureRoute('chats',     '/chats',      <Chats />)}
            {featureRoute('leads',     '/leads',      <Leads />)}
            {featureRoute('users',     '/users',      <Users />)}
            <Route path="/follow-ups"   element={<FollowUps />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="*" element={<Navigate to={features.includes('dashboard') ? '/app' : (features.length > 0 ? `/app/${features[0]}` : '/app')} replace />} />
          </Routes>
        </main>
      </div>
    </SocketProvider>
  );
}

// ─── Support Agent Layout ─────────────────────────────────────────────────────
function SupportAgentLayout() {
  const { user } = useAuth();
  const allowedApps = user?.allowedApps || [];
  const isOrgAdmin = user?.role === 'orgadmin';

  if (!allowedApps.includes('support_agent') && user?.role !== 'superadmin') {
    return <Navigate to="/app" replace />;
  }

  return (
    <SocketProvider>
      <div style={{ display: 'flex', minHeight: '100vh', background: '#0f172a' }}>
        <SupportAgentSidebar />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Routes>
            <Route path="/"              element={<SupportTickets />} />
            <Route path="/whatsapp"      element={<BotManagement />} />
            {isOrgAdmin && <Route path="/db-connectors" element={<SupportConfig />} />}
            <Route path="/bot-config"    element={<BotManagement />} />
            <Route path="*"              element={<Navigate to="/support-agent" replace />} />
          </Routes>
        </main>
      </div>
    </SocketProvider>
  );
}

// ─── Private Layout Gate ──────────────────────────────────────────────────────
function PrivateLayout() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ color: '#64748b', padding: 40, background: '#0f172a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      Loading...
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  if (user.role === 'superadmin') return <SuperAdminLayout />;
  return <OrgLayout />;
}

// ─── Support Agent Private Gate ───────────────────────────────────────────────
function SupportPrivateLayout() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ color: '#64748b', padding: 40, background: '#0f172a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      Loading...
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <SupportAgentLayout />;
}

// ─── Public Route Guard ───────────────────────────────────────────────────────
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/gateway" replace />;
  return children;
}

// ─── Require Auth Guard ───────────────────────────────────────────────────────
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ color: '#64748b', padding: 40, background: '#0a0a0a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      Loading...
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/"                   element={<LandingPage />} />
          <Route path="/login"             element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/gateway"           element={<RequireAuth><Gateway /></RequireAuth>} />
          <Route path="/broadcast"         element={<RequireAuth><Broadcaster /></RequireAuth>} />
          <Route path="/app/*"             element={<PrivateLayout />} />
          <Route path="/support-agent/*"   element={<SupportPrivateLayout />} />
          <Route path="*"                  element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
